import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import axios from 'axios';

function HotelBanner() {
    const [destination, setDestination] = useState('');
    const [checkIn, setCheckIn] = useState(new Date());
    const [checkOut, setCheckOut] = useState(new Date(Date.now() + 86400000)); // +1 day
    const [guests, setGuests] = useState({ adults: 2, children: 0, rooms: 1 });
    const [showGuestDropdown, setShowGuestDropdown] = useState(false);
    
    // Banner content state
    const [bannerData, setBannerData] = useState({
        title: 'Find your perfect hotel',
        subtitle: 'Search and compare hotels from hundreds of travel sites',
        background_image: null,
        button_text: 'Search Hotels'
    });
    const [loading, setLoading] = useState(true);

    // Fetch banner data from API
    useEffect(() => {
        const fetchBannerData = async () => {
            try {
                const response = await axios.get('/api/hotel/page-data/');
                if (response.data && response.data.banner) {
                    setBannerData({
                        title: response.data.banner.title,
                        subtitle: response.data.banner.subtitle,
                        background_image: response.data.banner.background_image,
                        button_text: response.data.banner.button_text || 'Search Hotels'
                    });
                }
            } catch (error) {
                console.error('Error fetching banner data:', error);
                // Keep default values if API fails
            } finally {
                setLoading(false);
            }
        };

        fetchBannerData();
    }, []);

    const handleSearch = (e) => {
        e.preventDefault();
        console.log('Searching hotels:', { destination, checkIn, checkOut, guests });
        // TODO: Implement search functionality
    };

    const updateGuests = (type, operation) => {
        setGuests(prev => ({
            ...prev,
            [type]: operation === 'add' 
                ? prev[type] + 1 
                : Math.max(type === 'rooms' ? 1 : 0, prev[type] - 1)
        }));
    };

    // Show loading state
    if (loading) {
        return (
            <div className="hotel-banner-area">
                <div className="hotel-banner-bg" style={{
                    backgroundColor: '#f8f9fa',
                    minHeight: '600px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <div className="text-center">
                        <div className="spinner-border text-primary" role="status">
                            <span className="sr-only">Loading...</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const backgroundImage = bannerData.background_image 
        ? bannerData.background_image 
        : '/assets/img/banner/banner_1.jpg';

    return (
        <div className="hotel-banner-area">
            <div className="hotel-banner-bg" style={{
                backgroundImage: `url(${backgroundImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                minHeight: '600px',
                position: 'relative'
            }}>
                <div className="banner-overlay"></div>
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-lg-10">
                            <div className="banner-content text-center">
                                <h1 className="banner-title text-white mb-4">
                                    {bannerData.title}
                                </h1>
                                <p className="banner-subtitle text-white mb-5">
                                    {bannerData.subtitle}
                                </p>
                                
                                <div className="hotel-search-form">
                                    <form onSubmit={handleSearch} className="search-form-wrapper">
                                        <div className="search-form-container">
                                            {/* Destination Input */}
                                            <div className="search-field destination-field">
                                                <div className="field-icon">
                                                    <i className="fas fa-map-marker-alt"></i>
                                                </div>
                                                <div className="field-content">
                                                    <label>Destination</label>
                                                    <input
                                                        type="text"
                                                        placeholder="Where are you going?"
                                                        value={destination}
                                                        onChange={(e) => setDestination(e.target.value)}
                                                        className="form-control"
                                                    />
                                                </div>
                                            </div>

                                            {/* Check-in Date */}
                                            <div className="search-field date-field">
                                                <div className="field-icon">
                                                    <i className="fas fa-calendar-alt"></i>
                                                </div>
                                                <div className="field-content">
                                                    <label>Check-in</label>
                                                    <DatePicker
                                                        selected={checkIn}
                                                        onChange={setCheckIn}
                                                        dateFormat="MMM dd"
                                                        minDate={new Date()}
                                                        className="form-control"
                                                    />
                                                </div>
                                            </div>

                                            {/* Check-out Date */}
                                            <div className="search-field date-field">
                                                <div className="field-icon">
                                                    <i className="fas fa-calendar-alt"></i>
                                                </div>
                                                <div className="field-content">
                                                    <label>Check-out</label>
                                                    <DatePicker
                                                        selected={checkOut}
                                                        onChange={setCheckOut}
                                                        dateFormat="MMM dd"
                                                        minDate={checkIn}
                                                        className="form-control"
                                                    />
                                                </div>
                                            </div>

                                            {/* Guests & Rooms */}
                                            <div className="search-field guests-field">
                                                <div className="field-icon">
                                                    <i className="fas fa-users"></i>
                                                </div>
                                                <div className="field-content">
                                                    <label>Guests & rooms</label>
                                                    <div 
                                                        className="guests-input form-control"
                                                        onClick={() => setShowGuestDropdown(!showGuestDropdown)}
                                                    >
                                                        {guests.adults + guests.children} guests, {guests.rooms} room
                                                        <i className="fas fa-chevron-down"></i>
                                                    </div>
                                                    
                                                    {showGuestDropdown && (
                                                        <div className="guests-dropdown">
                                                            <div className="guest-option">
                                                                <span>Adults</span>
                                                                <div className="guest-counter">
                                                                    <button 
                                                                        type="button" 
                                                                        onClick={() => updateGuests('adults', 'subtract')}
                                                                        disabled={guests.adults <= 1}
                                                                    >-</button>
                                                                    <span>{guests.adults}</span>
                                                                    <button 
                                                                        type="button" 
                                                                        onClick={() => updateGuests('adults', 'add')}
                                                                    >+</button>
                                                                </div>
                                                            </div>
                                                            <div className="guest-option">
                                                                <span>Children</span>
                                                                <div className="guest-counter">
                                                                    <button 
                                                                        type="button" 
                                                                        onClick={() => updateGuests('children', 'subtract')}
                                                                        disabled={guests.children <= 0}
                                                                    >-</button>
                                                                    <span>{guests.children}</span>
                                                                    <button 
                                                                        type="button" 
                                                                        onClick={() => updateGuests('children', 'add')}
                                                                    >+</button>
                                                                </div>
                                                            </div>
                                                            <div className="guest-option">
                                                                <span>Rooms</span>
                                                                <div className="guest-counter">
                                                                    <button 
                                                                        type="button" 
                                                                        onClick={() => updateGuests('rooms', 'subtract')}
                                                                        disabled={guests.rooms <= 1}
                                                                    >-</button>
                                                                    <span>{guests.rooms}</span>
                                                                    <button 
                                                                        type="button" 
                                                                        onClick={() => updateGuests('rooms', 'add')}
                                                                    >+</button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Search Button */}
                                            <div className="search-field search-button">
                                                <button type="submit" className="btn btn-primary search-btn">
                                                    <i className="fas fa-search"></i>
                                                    {bannerData.button_text}
                                                </button>
                                            </div>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default HotelBanner;
